'use client'
import { Box, Flex, Group, Stack, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import React, { Suspense } from 'react'

import {
  ColorChangeCircle,
  SuggestPercentageBar,
  SuggestType,
} from '@/app/[locale]/price/stocks/[symbol]/_components/AnalystRatings/component'
import { AnalystRatingsFallback } from '@/app/[locale]/price/stocks/[symbol]/_components/AnalystRatings/fallback'
import { useStockContext } from '@/app/[locale]/price/stocks/[symbol]/_contexts/StockContext'
import IconTrendingDown from '@/assets/icons/chart-trending-down.svg'
import IconTrendingUp from '@/assets/icons/chart-trending-up.svg'
import IconMinus from '@/assets/icons/icon-minus.svg'
import IconTarget from '@/assets/icons/target.svg'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { DEFAULT_CURRENCY, getCurrencyInfo } from '@/configs/currencies'
import { useSsrContext } from '@/contexts/ssr'
import { useStockRatingsById } from '@/services/stock/ratings'
import { formatPercentageColor, formatPercentageWithPrefixLimit } from '@/utils/number/format'

export const colorMap = {
  sell: {
    bg: 'rgba(224, 72, 92, 0.2)',
    child: 'var(--content-danger-bold)',
    icon: <IconTrendingDown w={20} h={20} />,
  },
  hold: {
    bg: 'rgba(255, 255, 255, 0.1)',
    child: 'var(--content-secondary)',
    icon: <IconMinus w={20} h={20} />,
  },
  buy: {
    bg: 'rgba(0, 166, 140, 0.2)',
    child: 'var(--content-success-bold)',
    icon: <IconTrendingUp w={20} h={20} />,
  },
}

function AnalystRatingsContent() {
  const t = useTranslations('analystRating')
  const { stock } = useStockContext()
  const { data: ratings } = useStockRatingsById(stock.token_id)
  const { buy, hold, sell, target_price, est_return, rating } = ratings

  const stockSuggest = rating.toLowerCase() as SuggestType
  const { selectedCurrency } = useSsrContext()
  const currentCurrency = getCurrencyInfo(selectedCurrency) || DEFAULT_CURRENCY

  const returnSuggest = est_return > 0 ? 'buy' : est_return < 0 ? 'sell' : 'hold'
  const estReturn = formatPercentageWithPrefixLimit(est_return, true)

  const estReturnChangeColor = formatPercentageColor(Number(estReturn.replace('%', '')))

  return (
    <Box
      mt={{ base: 40, medium: 80 }}
      style={{
        borderRadius: 12,
        width: '100%',
      }}
    >
      <Text component="h2" variant="heading2" mb={16}>
        {t('title')}
      </Text>
      <Flex
        direction={{ base: 'column', medium: 'row', large: 'column', contentLimit: 'row' }}
        gap={{ base: 0, medium: 40 }}
        align={{
          base: 'flex-start',
          medium: 'center',
          large: 'flex-start',
          contentLimit: 'flex-start',
        }}
      >
        <Flex gap={{ base: 12, medium: 16 }} py={16} flex={1}>
          <ColorChangeCircle suggest={stockSuggest} w={72} h={72}>
            <Text c={colorMap[stockSuggest].child} variant="heading1">
              {t(stockSuggest)}
            </Text>
          </ColorChangeCircle>
          <Stack gap={12}>
            {[
              { value: buy, suggest: 'buy' },
              { value: hold, suggest: 'hold' },
              { value: sell, suggest: 'sell' },
            ].map(stockSuggest => {
              return (
                <SuggestPercentageBar
                  key={stockSuggest.suggest}
                  value={stockSuggest.value}
                  suggest={stockSuggest.suggest as SuggestType}
                />
              )
            })}
          </Stack>
        </Flex>
        <Flex py={12} gap={16}>
          <Group gap={8} wrap="nowrap">
            <Flex
              justify={'center'}
              align={'center'}
              w={36}
              h={36}
              style={{
                borderRadius: 40,
                background: 'var(--bg-button-tertiary-default)',
              }}
            >
              <IconTarget w={20} h={20} />
            </Flex>
            <Stack w={'max-content'} gap={0}>
              <Text c={'var(--content-secondary)'} variant="body">
                {t('target_price')}
              </Text>
              <Text c={'var(--content-primary)'} variant="heading3">
                {`${currentCurrency.symbol}${(target_price || 0).toFixed(2)}`}
              </Text>
            </Stack>
          </Group>
          <Group gap={8} wrap="nowrap">
            <ColorChangeCircle w={36} h={36} suggest={returnSuggest}>
              {colorMap[returnSuggest].icon}
            </ColorChangeCircle>
            <Stack w={'max-content'} gap={0}>
              <Text c={'var(--content-secondary)'} variant="body">
                {t('est_return')}
              </Text>
              <Text c={estReturnChangeColor} variant="heading3">
                {`${estReturn}`}
              </Text>
            </Stack>
          </Group>
        </Flex>
      </Flex>
    </Box>
  )
}

function AnalystRatings() {
  return (
    <Suspense fallback={<AnalystRatingsFallback />}>
      <AnalystRatingsContent />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(AnalystRatings)
